import { Link } from "react-router-dom";
import { ArrowRight, Phone, CalendarCheck, ShieldCheck, BadgeCheck, Award, Users, Sparkles } from "lucide-react";
import { BRAND, SERVICES, STATS, FAQS } from "../lib/constants";
import HeroShield from "../components/HeroShield";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const featureServices = SERVICES.slice(0, 8);

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center hero-bg-anim"
            style={{ backgroundImage: `url('${BRAND.images.heroBg}')` }}
          />
          <div className="absolute inset-0 hero-overlay" />
          <div
            className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
            style={{ backgroundImage: `url('${BRAND.images.shieldPattern}')`, backgroundSize: "cover", backgroundPosition: "center" }}
          />
        </div>
        <HeroShield position="right" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-32 lg:pt-32 lg:pb-40">
          <div className="max-w-3xl">
            <span className="eyebrow text-[color:var(--isa-gold)] reveal" data-testid="hero-eyebrow">
              Independent. Trusted. Personalized.
            </span>
            <h1 className="reveal reveal-delay-1 mt-6 font-serif text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight">
              Protect what matters most,<br />
              <span className="text-[color:var(--isa-gold)] italic">build what comes next.</span>
            </h1>
            <p className="reveal reveal-delay-2 mt-7 max-w-2xl text-base lg:text-lg font-sans text-white/80 leading-relaxed">
              Personalized insurance and retirement-focused financial solutions for individuals,
              families, and business owners. Real guidance. Real coverage. No pressure — ever.
            </p>

            <div className="reveal reveal-delay-3 mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/contact?form=quote" className="btn-primary" data-testid="hero-quote-button">
                Request a Free Quote <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link to="/contact?form=consult" className="btn-outline-gold" data-testid="hero-consult-button">
                <CalendarCheck className="w-4 h-4" strokeWidth={1.8} />
                Book a Consultation
              </Link>
              <a href={`tel:${BRAND.phoneTel}`} className="btn-outline-gold" data-testid="hero-call-button">
                <Phone className="w-4 h-4" strokeWidth={1.8} />
                Call {BRAND.phone}
              </a>
            </div>

            <div className="reveal reveal-delay-4 mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.18em] text-white/70 font-sans">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[color:var(--isa-gold)]" strokeWidth={1.6} /> Licensed & Independent</span>
              <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[color:var(--isa-gold)]" strokeWidth={1.6} /> A-Rated Carriers</span>
              <span className="flex items-center gap-2"><Award className="w-4 h-4 text-[color:var(--isa-gold)]" strokeWidth={1.6} /> Community-Focused</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS BAR ===== */}
      <section className="bg-[color:var(--isa-soft-gray)] border-y border-gray-200" data-testid="home-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center md:text-left">
              <p className="font-serif text-4xl lg:text-5xl text-[color:var(--isa-navy)]">
                {s.value}
              </p>
              <p className="mt-2 text-xs lg:text-sm font-sans uppercase tracking-[0.18em] text-gray-500">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SERVICES GRID ===== */}
      <section className="py-24 lg:py-32 bg-white" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
            <div className="lg:col-span-7">
              <span className="eyebrow">Coverage That Fits Every Stage</span>
              <h2 className="mt-5 section-heading text-4xl lg:text-5xl">
                Insurance and financial solutions, built around <em className="not-italic text-[color:var(--isa-gold)]">your life</em>.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <p className="text-base font-sans text-gray-600 leading-relaxed">
                From your first apartment to your retirement, your first car to your growing business —
                we'll match the right coverage to the moment you're in.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200">
            {featureServices.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.slug}
                  className="group bg-white p-8 lg:p-10 card-lift relative"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="w-12 h-12 inline-flex items-center justify-center border border-[color:var(--isa-gold)] text-[color:var(--isa-gold)] mb-6">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl text-[color:var(--isa-navy)] leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm font-sans text-gray-600 leading-relaxed">
                    {s.short}
                  </p>
                  <Link
                    to="/services"
                    className="mt-6 inline-flex items-center gap-1.5 text-xs font-sans font-semibold uppercase tracking-[0.18em] text-[color:var(--isa-navy)] group-hover:text-[color:var(--isa-gold)] transition-colors"
                  >
                    Learn More <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link to="/services" className="btn-outline-navy" data-testid="home-all-services-button">
              View All Services <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== MISSION / WHY US ===== */}
      <section className="relative overflow-hidden bg-[color:var(--isa-navy)] text-white" data-testid="home-mission">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url('${BRAND.images.servicesTexture}')` }}
        />
        <div className="absolute inset-0 bg-[color:var(--isa-navy)]/85" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-6">
              <span className="eyebrow text-[color:var(--isa-gold)]">Our Promise</span>
              <h2 className="mt-5 font-serif text-4xl lg:text-5xl leading-tight tracking-tight">
                Insurance should never feel <em className="text-[color:var(--isa-gold)] not-italic">confusing</em>.
              </h2>
              <p className="mt-6 text-base lg:text-lg font-sans text-white/80 leading-relaxed">
                Our mission is simple: help individuals, families, and business owners protect what
                matters most while building a more secure financial future. Backed by a strong
                foundation in tax and financial services, we understand the importance of
                personalized guidance, dependable coverage, and long-term planning.
              </p>
              <p className="mt-5 text-base font-sans text-white/70 leading-relaxed">
                When you work with us, you're not just another policy number — you're family.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link to="/about" className="btn-outline-gold" data-testid="mission-about-button">
                  Learn About Us
                </Link>
                <Link to="/contact?form=consult" className="btn-primary" data-testid="mission-consult-button">
                  Schedule a Conversation
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { icon: ShieldCheck, title: "Independent Advisors", body: "We compare carriers — you choose with confidence." },
                { icon: Users, title: "Family-First Approach", body: "Relationships before transactions, always." },
                { icon: Sparkles, title: "Tax-Advantaged Strategies", body: "Retirement and annuity options that protect your future." },
                { icon: Award, title: "Community-Rooted", body: "Local advisors who treat clients like family — every conversation, every claim." },
              ].map((b) => {
                const Icon = b.icon;
                return (
                  <div
                    key={b.title}
                    className="border border-white/15 bg-white/5 p-7 backdrop-blur-sm card-lift"
                  >
                    <Icon className="w-6 h-6 text-[color:var(--isa-gold)]" strokeWidth={1.5} />
                    <h3 className="mt-4 font-serif text-xl">{b.title}</h3>
                    <p className="mt-2 text-sm font-sans text-white/70 leading-relaxed">{b.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ PREVIEW ===== */}
      <section className="py-24 lg:py-32 bg-[color:var(--isa-soft-gray)]" data-testid="home-faq">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="eyebrow">Common Questions</span>
            <h2 className="mt-5 section-heading text-4xl lg:text-5xl">
              You ask. We <em className="not-italic text-[color:var(--isa-gold)]">explain</em>.
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3" data-testid="home-faq-accordion">
            {FAQS.slice(0, 5).map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white border border-gray-200 px-6"
              >
                <AccordionTrigger
                  className="font-serif text-lg lg:text-xl text-[color:var(--isa-navy)] text-left hover:no-underline py-5"
                  data-testid={`faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm font-sans text-gray-600 leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center">
            <Link to="/faq" className="btn-outline-navy" data-testid="home-all-faq-button">
              See All Questions <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== CTA STRIPE ===== */}
      <section className="bg-[color:var(--isa-navy)] text-white" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <span className="eyebrow text-[color:var(--isa-gold)]">Ready When You Are</span>
            <h2 className="mt-4 font-serif text-3xl lg:text-5xl leading-tight">
              Let's protect what matters — <em className="not-italic text-[color:var(--isa-gold)]">together</em>.
            </h2>
            <p className="mt-5 text-base font-sans text-white/75 max-w-2xl">
              Request a free quote, book a no-pressure consultation, or just give us a call.
              You'll always speak with a real, licensed advisor.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/contact?form=quote" className="btn-primary" data-testid="cta-quote-button">
              Request a Quote
            </Link>
            <a href={`tel:${BRAND.phoneTel}`} className="btn-outline-gold" data-testid="cta-call-button">
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
